import React, { useState } from 'react';
import axios from 'axios';
import { Download } from '@mui/icons-material';
import { Alert, Stack } from '@mui/material';
import { LocalizedButton as Button } from './i18n/LocalizedMui';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { normalizeLanguage } from '../i18n/languages';
import { translateInline } from '../i18n/inlineTranslations';
import PremiumTrialPopup from './PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

type Props = {
  apiBaseUrl: string;
  module: 'vedic' | 'lo-shu';
  fullName?: string;
  dob: string;
  mobile?: string;
};

type Scope = 'full' | 'vedic' | 'lo-shu';

const REPORT_NAME_NOTICE: Record<string, string> = {
  en: 'Add your full name to download a personalized numerology report. Phone number is optional.',
  hi: 'व्यक्तिगत अंक ज्योतिष रिपोर्ट डाउनलोड करने के लिए अपना पूरा नाम जोड़ें। फोन नंबर वैकल्पिक है।',
  ta: 'தனிப்பயன் எண் கணித அறிக்கையைப் பதிவிறக்க உங்கள் முழுப் பெயரைச் சேர்க்கவும். தொலைபேசி எண் விருப்பமானது.',
  te: 'వ్యక్తిగత సంఖ్యాశాస్త్ర నివేదికను డౌన్‌లోడ్ చేయడానికి మీ పూర్తి పేరును జోడించండి. ఫోన్ నంబర్ ఐచ్ఛికం.',
  mr: 'वैयक्तिक अंकशास्त्र अहवाल डाउनलोड करण्यासाठी आपले पूर्ण नाव जोडा. फोन नंबर ऐच्छिक आहे.',
  gu: 'વ્યક્તિગત અંકશાસ્ત્ર રિપોર્ટ ડાઉનલોડ કરવા માટે તમારું પૂરું નામ ઉમેરો. ફોન નંબર વૈકલ્પિક છે.',
  bn: 'ব্যক্তিগত সংখ্যাতত্ত্ব রিপোর্ট ডাউনলোড করতে আপনার পুরো নাম যোগ করুন। ফোন নম্বর ঐচ্ছিক।',
  kn: 'ವೈಯಕ್ತಿಕ ಸಂಖ್ಯಾಶಾಸ್ತ್ರ ವರದಿಯನ್ನು ಡೌನ್‌ಲೋಡ್ ಮಾಡಲು ನಿಮ್ಮ ಪೂರ್ಣ ಹೆಸರನ್ನು ಸೇರಿಸಿ. ಫೋನ್ ಸಂಖ್ಯೆ ಐಚ್ಛಿಕವಾಗಿದೆ.',
  ml: 'വ്യക്തിഗത സംഖ്യാശാസ്ത്ര റിപ്പോർട്ട് ഡൗൺലോഡ് ചെയ്യാൻ നിങ്ങളുടെ പൂർണ്ണ പേര് ചേർക്കുക. ഫോൺ നമ്പർ ഐച്ഛികമാണ്.',
  pa: 'ਨਿੱਜੀ ਅੰਕ ਵਿਗਿਆਨ ਰਿਪੋਰਟ ਡਾਊਨਲੋਡ ਕਰਨ ਲਈ ਆਪਣਾ ਪੂਰਾ ਨਾਮ ਸ਼ਾਮਲ ਕਰੋ। ਫੋਨ ਨੰਬਰ ਵਿਕਲਪਿਕ ਹੈ।',
  or: 'ବ୍ୟକ୍ତିଗତ ସଂଖ୍ୟାତତ୍ତ୍ୱ ରିପୋର୍ଟ ଡାଉନଲୋଡ୍ କରିବାକୁ ଆପଣଙ୍କ ପୂର୍ଣ୍ଣ ନାମ ଯୋଡନ୍ତୁ। ଫୋନ୍ ନମ୍ବର ବୈକଳ୍ପିକ।',
  as: 'ব্যক্তিগত সংখ্যাতত্ত্ব প্ৰতিবেদন ডাউনলোড কৰিবলৈ আপোনাৰ সম্পূৰ্ণ নাম যোগ কৰক। ফোন নম্বৰ ঐচ্ছিক।',
  ur: 'ذاتی عددی رپورٹ ڈاؤن لوڈ کرنے کے لیے اپنا پورا نام شامل کریں۔ فون نمبر اختیاری ہے۔',
};

const safeFilePart = (value: string, fallback: string) => (
  String(value || fallback).replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_')
);

export default function NumerologyReportButtons({ apiBaseUrl, module, fullName = '', dob, mobile = '' }: Props) {
  const { i18n } = useTranslation();
  const { state: authState } = useAuth();
  const { dateDisplayFormat } = useUserPreferences();
  const [busy, setBusy] = useState<Scope | null>(null);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const text = (en: string, hi: string) => translateInline(language, en, hi);
  const moduleScope: Scope = module;
  const normalizedName = fullName.trim();
  const nameNotice = REPORT_NAME_NOTICE[language] || REPORT_NAME_NOTICE.en;

  const saveBlob = (scope: Scope, blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeFilePart(normalizedName, 'numerology')}_${scope === 'lo-shu' ? 'lo_shu' : scope === 'vedic' ? 'vedic_numerology' : 'full_numerology'}_report.pdf`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const requestPdf = async (scope: Scope, trialAccessToken?: string) => {
    setBusy(scope);
    try {
      const params = new URLSearchParams({ dob, fullName: normalizedName, scope, language, dateDisplayFormat });
      if (mobile.trim()) params.set('mobile', mobile.trim());
      const response = await axios.get(`${apiBaseUrl}/numerology/report/pdf?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${authState.token}`,
          ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {}),
        },
        responseType: 'blob',
      });
      return { ...response, data: { blob: response.data as Blob } };
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        const payload = JSON.parse(await error.response.data.text().catch(() => '{}'));
        error.response.data = payload;
      }
      throw error;
    } finally {
      setBusy(null);
    }
  };

  const download = async (scope: Scope) => {
    if (!normalizedName) {
      window.alert(nameNotice);
      return;
    }
    if (!authState.isAuthenticated || !authState.token) {
      window.location.assign('/login');
      return;
    }
    try {
      await checkFeatureAccess({
        featureKey: 'BRANDED_PDF',
        apiCall: (trialAccessToken?: string) => requestPdf(scope, trialAccessToken),
        onSuccess: (data: any) => saveBlob(scope, data.blob),
      });
    } catch (error: any) {
      window.alert(error.message || text('Could not generate the report.', 'रिपोर्ट तैयार नहीं हो सकी।'));
    }
  };

  return (
    <>
    {!normalizedName ? (
      <Alert severity="info">{nameNotice}</Alert>
    ) : (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
      <Button
        variant="contained"
        startIcon={<Download />}
        disabled={!dob || busy !== null || trialLoading}
        onClick={() => download('full')}
      >
        {busy === 'full'
          ? text('Preparing full report...', 'पूरी रिपोर्ट तैयार हो रही है...')
          : text('Download Full Numerology Report', 'पूरी अंक ज्योतिष रिपोर्ट डाउनलोड करें')}
      </Button>
      <Button
        variant="outlined"
        startIcon={<Download />}
        disabled={!dob || busy !== null || trialLoading}
        onClick={() => download(moduleScope)}
      >
        {busy === moduleScope
          ? module === 'vedic'
            ? text('Preparing Vedic report...', 'वैदिक रिपोर्ट तैयार हो रही है...')
            : text('Preparing Lo Shu report...', 'लो शू रिपोर्ट तैयार हो रही है...')
          : module === 'vedic'
            ? text('Download Vedic Numerology Only Report', 'केवल वैदिक अंक ज्योतिष रिपोर्ट डाउनलोड करें')
            : text('Download Lo Shu Only Report', 'केवल लो शू रिपोर्ट डाउनलोड करें')}
      </Button>
    </Stack>
    )}
    <PremiumTrialPopup
      open={popup.open}
      featureName={popup.featureName}
      used={popup.used}
      total={popup.total}
      remaining={popup.remaining}
      loading={trialLoading || busy !== null}
      onCancel={closePopup}
      onContinue={popup.nextAction || (() => Promise.resolve())}
    />
    </>
  );
}
